import React, { useEffect } from "react";
import {  Button,
  TextField,
  Typography,
  Box,
  Paper,
  Stack,
  alpha,
  useTheme,
  Divider,} from "@mui/material";
import Grid from "@mui/material/Grid";
import {
  Print,
  Save,
  Delete,
  AssignmentTurnedIn,
  DateRange,
  LocationOn,
  Height,
  Straighten,
} from "@mui/icons-material";
import { useVoyageContextV2 } from "../../Contexts/VoyageContextV2";
import { useTranslation } from "react-i18next";
import { getSelectedCargo, getSelectedTrip } from "../../utils/voyageSelection";

const SlopBeforeLoading = ({
  handlePrintButton,
  handleSaveButton,
  handleDeleteSlopData,
  date,
  readOnly,
}) => {
  const { t } = useTranslation("slop");
  const theme = useTheme();
  const { selectedTripId, selectedCargoIndex, currentVoyage } =
    useVoyageContextV2();

  const trip = getSelectedTrip(currentVoyage, selectedTripId);
  const cargo = getSelectedCargo(
    currentVoyage,
    selectedTripId,
    selectedCargoIndex
  );
  const slopDataBefore = cargo?.slopData?.before;
  const draftArrival = cargo?.activities?.["draft-arrival"]?.remarks;

  useEffect(() => {
    if (slopDataBefore) {

      // Fyll i mätvärden
      Object.keys(slopDataBefore.measurements || {}).forEach((measurement) => {
        const measurementData = slopDataBefore.measurements[measurement];
        Object.keys(measurementData).forEach((key) => {
          const elementId = `${measurement
            .toLowerCase()
            .replace(/\s+/g, "-")}-${key.toLowerCase()}`;
          const element = document.getElementById(elementId);
          if (element && measurementData[key]) {
            element.value = measurementData[key];
          }
        });
      });

      // Fyll i remarks
      const remarksField = document.getElementById("remarksbefore");
      if (remarksField && slopDataBefore.remarks) {
        remarksField.value = slopDataBefore.remarks;
      }
    }
  }, [slopDataBefore]);

  const InfoChip = ({ icon, label, value }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1 }}>
      <Box sx={{ color: "primary.main", display: "flex" }}>{icon}</Box>
      <Box>
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{ fontWeight: 700, textTransform: "uppercase", display: "block" }}
        >
          {label}
        </Typography>
        <Typography variant="body2" fontWeight="700">
          {value || "—"}
        </Typography>
      </Box>
    </Box>
  );

  const calculateTrim = () => {
    const aft = parseFloat(draftArrival?.aft);
    const fwd = parseFloat(draftArrival?.fwd);
    if (isNaN(aft) || isNaN(fwd)) return "N/A";
    return (aft - fwd).toFixed(2) + " m";
  };

  return (
    <Box>
      {/* 1. Voyage & Draft Summary Header */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: alpha(theme.palette.primary.main, 0.02),
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <AssignmentTurnedIn color="success" fontSize="small" />
          <Typography variant="subtitle2" fontWeight="800" color="success.main">
            {t("before").toUpperCase()} - ARRIVAL FIGURES
          </Typography>
        </Stack>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <InfoChip
              icon={<DateRange fontSize="small" />}
              label={t("voyageInfo.date")}
              value={date}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <InfoChip
              icon={<LocationOn fontSize="small" />}
              label={t("voyageInfo.port")}
              value={`${trip?.to} (${trip?.jetty || "N/A"})`}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <InfoChip
              icon={<Height fontSize="small" />}
              label="Current Draft (A/F)"
              value={`${draftArrival?.aft || "—"} / ${
                draftArrival?.fwd || "—"
              } m`}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <InfoChip
              icon={<Straighten fontSize="small" />}
              label="Calculated Trim"
              value={calculateTrim()}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* 2. Measurements Grid */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", px: 2, mb: 1, opacity: 0.6 }}>
          <Typography variant="caption" sx={{ flex: 2, fontWeight: 800 }}>
            MEASUREMENT TYPE
          </Typography>
          <Typography
            variant="caption"
            sx={{ flex: 1, fontWeight: 800, textAlign: "center" }}
          >
            PORT (P)
          </Typography>
          <Typography
            variant="caption"
            sx={{ flex: 1, fontWeight: 800, textAlign: "center" }}
          >
            STBD (S)
          </Typography>
          <Typography
            variant="caption"
            sx={{ flex: 1, fontWeight: 800, textAlign: "center" }}
          >
            SLOP TANK
          </Typography>
          <Typography
            variant="caption"
            sx={{ flex: 2, fontWeight: 800, textAlign: "center" }}
          >
            REMARKS
          </Typography>
        </Box>

        <Stack spacing={1}>
          {[
            "UTI Ullage",
            "Corr Ullage",
            "GOV (m3)",
            "Last Cargo",
            "UTI Interface",
            "Corr Interface",
            "Water GOV",
          ].map((measurement, index) => {
            const baseId = measurement.toLowerCase().replace(/\s+/g, "-");
            return (
              <Paper
                key={index}
                elevation={0}
                sx={{
                  p: 1.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.01),
                  },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ flex: 2, fontWeight: 700, color: "text.primary" }}
                >
                  {measurement}
                </Typography>

                <Box sx={{ flex: 1 }}>
                  <TextField
                    id={`${baseId}-p`}
                    fullWidth
                    size="small"
                    variant="outlined"
                    disabled={readOnly}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Box>

                <Box sx={{ flex: 1 }}>
                  <TextField
                    id={`${baseId}-s`}
                    fullWidth
                    size="small"
                    variant="outlined"
                    disabled={readOnly}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Box>

                <Box sx={{ flex: 1 }}>
                  <TextField
                    id={`${baseId}-cargo`}
                    fullWidth
                    size="small"
                    variant="outlined"
                    disabled={readOnly}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Box>

                <Box sx={{ flex: 2 }}>
                  <TextField
                    id={`${baseId}-remarks`}
                    fullWidth
                    size="small"
                    variant="outlined"
                    disabled={readOnly}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Box>
              </Paper>
            );
          })}
        </Stack>
      </Box>

      {/* 3. General Remarks */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle2" fontWeight="800" sx={{ mb: 1, px: 1 }}>
          {t("table.remarks").toUpperCase()}
        </Typography>
        <TextField
          id="remarksbefore"
          fullWidth
          multiline
          rows={3}
          placeholder="Enter additional remarks here..."
          variant="outlined"
          disabled={readOnly}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
        />
      </Box>

      <Divider sx={{ mb: 4 }} />

      {/* 4. Action Buttons */}
      <Stack direction="row" spacing={2} justifyContent="center">
        <Button
          variant="contained"
          size="large"
          startIcon={<Print />}
          onClick={handlePrintButton}
          sx={{
            px: 4,
            borderRadius: 3,
            fontWeight: 800,
            bgcolor: "primary.main",
          }}
        >
          {t("printButton")}
        </Button>
        <Button
          variant="contained"
          size="large"
          color="success"
          startIcon={<Save />}
          onClick={handleSaveButton}
          disabled={readOnly}
          sx={{ px: 4, borderRadius: 3, fontWeight: 800 }}
        >
          {t("saveButton")}
        </Button>
        <Button
          variant="outlined"
          size="large"
          color="error"
          startIcon={<Delete />}
          onClick={handleDeleteSlopData}
          disabled={readOnly}
          sx={{
            px: 4,
            borderRadius: 3,
            fontWeight: 800,
            borderWidth: 2,
            "&:hover": { borderWidth: 2 },
          }}
        >
          {t("deleteButton")}
        </Button>
      </Stack>
    </Box>
  );
};

export default SlopBeforeLoading;
